import { Flex, Select, Tooltip } from "antd";
import { Note } from "../../models/BdaApplication";
import { Alert, Button, Col, Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import IconButton from "../common/IconButton";
import { Applications } from "../../api/Applications";
import { Option } from "../../models/generics";
import { useToasts } from "../../contexts/toasts";
import classNames from "classnames";
import AppLoader from "../common/AppLoader";

type Props = {
  notes?: Note[];
  initialPath?: 'service' | 'workflow';
  onCancel?: VoidFunction;
  onSubmit?: (key: string, notes: Note[]) => void;
  readOnly?: boolean;
}

interface AnnotationLevel {
  options?: Option[];
  value?: string;
  suggestions?: Record<string, string>;
}

interface AnnotationItem {
  suggestion?: string;
  suggestionLevel?: number;
  editable?: boolean;
  loading?: boolean;
  error?: boolean;
  [level: number]: AnnotationLevel;
}

function AnnotationsForm({ notes, initialPath, onCancel, onSubmit, readOnly }: Props) {
  const [annotationItems, setAnnotationItems] = useState<AnnotationItem[]>();
  const { showToast } = useToasts();

  const getOptions = useCallback((path?: string, index = 0, level = 0) => {
    const queryParams = new URLSearchParams([['path', path || initialPath!]]);

    Applications.getAnnotations(queryParams)
      .then(
        ({ data }) => {
          setAnnotationItems(curr => {
            const newItems = [...curr!];
            newItems[index][level].options = data.map(note => ({ label: note.name || note.value!, value: note.key || note.value! }));
            const hasSuggestions = data.some(note => note.suggestion);
            if (hasSuggestions) {
              newItems[index][level].suggestions = data.reduce((acc, curr) => {
                if (!curr.value || !curr.suggestion) return acc;
                acc[curr.value!] = curr.suggestion!;
                return acc;
              }, {} as Record<string, string>);
            }
            newItems[index].loading = false;
            return newItems;
          });
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('annotations-error', message, `Notes Error ${status ? `(${status})` : ''}`, 'danger');
          setAnnotationItems(curr => {
            const newItems = [...curr!];
            newItems[index].error = true;
            return newItems;
          });
        }
      )
  }, [initialPath, showToast]);

  useEffect(() => {
    setAnnotationItems(notes?.map(note => {
      const item: AnnotationItem = { suggestion: note.suggestion };
      note.path.split(",").forEach((value, index) => { item[index] = { value } });
      return item;
    }) || []);
  }, [notes]);

  const handleAddNote = useCallback(() => {
    setAnnotationItems(curr => {
      const newItems = curr!.concat({ editable: true, 0: {}, 1: {}, 2: {} });
      getOptions(undefined, newItems.length - 1);
      return newItems;
    });
  }, [getOptions]);

  const handleRemoveNote = useCallback((index: number) => () => {
    setAnnotationItems(curr => {
      const newItems = [...curr!];
      newItems.splice(index, 1);
      return newItems;
    });
  }, []);

  const reloadNotes = useCallback((index: number) => () => {
    setAnnotationItems(curr => {
      const newItems = [...curr!];
      newItems[index].error = false;
      return newItems;
    });
    getOptions(undefined, index);
  }, [getOptions]);

  const handleSelectChange = useCallback((index: number, level: number) => (value: string) => {
    setAnnotationItems(curr => {
      const newItems = [...curr!];
      newItems[index][level].value = value;
      newItems[index].suggestion = undefined;
      let checkIndex = level + 1;
      while (newItems[index][checkIndex]) {
        newItems[index][checkIndex] = {};
        checkIndex++;
      }
      if (newItems[index][level].suggestions?.[value]) {
        newItems[index].suggestion = newItems[index][level].suggestions![value];
        newItems[index].suggestionLevel = level;
      }
      else {
        const currentPath = (Object.entries(newItems[index]) as [any, any][])
          .filter(([key, _value]) => !Number.isNaN(+key))
          .sort((a, b) => a[0] - b[0])
          .map(entry => (entry[1] as AnnotationLevel).value)
          .filter((_, index) => index <= level)
          .join(',');
        const path = `${initialPath},${currentPath}`;
        newItems[index].loading = true;
        getOptions(path, index, level + 1);
      }
      return newItems;
    });
  }, [getOptions, initialPath]);

  const handleSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    const notes: Note[] = annotationItems!.map(item => {
      const path = (Object.entries(item) as [any, any][])
        .filter(([key, _value]) => !Number.isNaN(+key))
        .map(entry => (entry[1] as AnnotationLevel).value)
        .join(',');

      return {
        path,
        suggestion: item.suggestion || "",
        // personalNotes: item.personalNotes || "",
        // json: item.json || "",
      }
    });
    onSubmit?.(initialPath!, notes);
  }, [onSubmit, annotationItems, initialPath]);

  // some annotations cannot be submitted
  const disableSubmit = useMemo(() => {
    return (annotationItems || []).filter((item) => item.editable && !item.suggestion).length > 0;
  }, [annotationItems]);

  const renderAnnotationTitle = useCallback((item: AnnotationItem) => {
    const annotationValues: string[] = [];
    if (item[0] && item[0].value) annotationValues.push(item[0].value);
    if (item[1] && item[1].value) annotationValues.push(item[1].value);
    if (item[2] && item[2].value) annotationValues.push(item[2].value);
    return annotationValues.join(",");
  }, []);

  const renderAnnotations = useCallback(() => {
    if (annotationItems!.length === 0) return (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No notes found.</span>
      </Alert>
    )

    return annotationItems!.map((annotation, index) => (
      <div key={index} className={classNames("mb-2 border rounded", { 'p-2': annotation.editable })}>
        {!annotation.editable
          ? (
            <Flex align="center" className="rounded bg-light text-primary p-1 px-2">
              <Tooltip title={renderAnnotationTitle(annotation)}>
                <FontAwesomeIcon className="ms-1" icon={solid('info-circle')} />
              </Tooltip>
              <span className="ms-2">{annotation.suggestion}</span>
              {onSubmit && (
                <IconButton className="ms-auto p-2" type="button" tooltip="Remove" onClick={handleRemoveNote(index)}>
                  <FontAwesomeIcon icon={solid('times')} />
                </IconButton>
              )}
            </Flex>
          )
          : (
            <Flex align="center">
              {!annotation.error && (
                <Col md={3}>
                  <Select
                    className="w-100"
                    placeholder="Choose note value 1"
                    value={annotation[0]?.value}
                    onChange={handleSelectChange(index, 0)}
                    loading={!annotation[0]?.options}
                    options={annotation[0]?.options}
                  />
                </Col>
              )}
              {annotation[0]?.value && (!annotation.loading || annotation[1]?.value) && (annotation.suggestionLevel === undefined || annotation.suggestionLevel >= 1 || !annotation.suggestion) && (
                <Col md={3} className="ps-2">
                  <Select
                    className="w-100"
                    placeholder="Choose note value 2"
                    value={annotation[1]?.value}
                    onChange={handleSelectChange(index, 1)}
                    options={annotation[1]?.options}
                  />
                </Col>
              )}
              {annotation[1]?.value && (!annotation.loading || annotation[2]?.value) && (annotation.suggestionLevel === undefined || annotation.suggestionLevel >= 2 || !annotation.suggestion) && (
                <Col md={3} className="ps-2">
                  <Select
                    className="w-100"
                    placeholder="Choose note value 3"
                    value={annotation[2]?.value}
                    onChange={handleSelectChange(index, 2)}
                    options={annotation[2]?.options}
                  />
                </Col>
              )}
              {annotation.error && (
                <Flex align="center" className="text-danger">
                  <FontAwesomeIcon icon={solid('exclamation-triangle')} />
                  <span className="ms-2">An error occurred while retrieving notes.</span>

                  <IconButton type="button" className="p-0 ms-4" onClick={reloadNotes(index)}>
                    <FontAwesomeIcon icon={solid('refresh')} />
                    <span className="ms-2">Try again</span>
                  </IconButton>
                </Flex>
              )}
              {annotation.suggestion && (
                <Tooltip title={annotation.suggestion} color="geekblue">
                  <FontAwesomeIcon icon={solid('info-circle')} className="ms-2 text-primary" size="lg" />
                </Tooltip>
              )}
              {annotation.loading && <AppLoader size={25} />}
              <Col className="text-end">
                <IconButton type="button" tooltip="Remove" onClick={handleRemoveNote(index)}>
                  <FontAwesomeIcon icon={solid('times')} />
                </IconButton>
              </Col>
            </Flex>
          )}
      </div>
    ))
  }, [handleSelectChange, annotationItems, handleRemoveNote, renderAnnotationTitle, reloadNotes, onSubmit]);

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <Modal.Body>
        {!readOnly && (
          <Flex align="middle" justify="end" className="mb-2">
            <Button type="button" variant="light" className="border px-3" onClick={handleAddNote} size="sm">
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">Add Note</span>
            </Button>
          </Flex>
        )}
        {annotationItems && renderAnnotations()}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button type="button" variant="light" size="sm" className="border" onClick={onCancel}>Close</Button>
        {!readOnly && (
          <Button type="submit" variant="success" size="sm" disabled={!initialPath || !annotationItems || disableSubmit}>
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        )}
      </Modal.Footer>
    </Form>
  )
}

export default memo(AnnotationsForm);